/**
 * Date-column group-by bucketing (fleet filter contract v3 Part II §13.3, rules M4-M6).
 *
 * - **M4**: granularities are Year · Quarter · Month · Week · Day. Week is Monday-based, identical
 *   to `{{weekStart}}` (§5.1) — a grid whose weeks start Sunday while its own `This week` filter
 *   starts Monday shows a row in one week and filters it into another.
 * - **M5**: buckets resolve in the STAFF-INTERACTIVE context (`America/New_York`), through the IANA
 *   zone (not a fixed offset) — reuses `viewQuery.ts`'s own wall-clock primitives rather than a
 *   second implementation of the exact DST-correctness logic that C28/SUP-001 already cost real
 *   time to get right once.
 * - **M6**: grouping never drops a row. A value that is null, empty, or fails to parse buckets into
 *   a single sentinel "(blank)" group — it is "null" per §4.2's own rules for the column's type,
 *   never a group of its own per distinct non-value.
 *
 * The worked trap (§13.3, stated so it isn't rediscovered): a row stamped `2026-08-19T01:00:00Z` is
 * 21:00 ET on the 18th. Grouped by Day it belongs under **August 18** — bucketing on the UTC date
 * instead would file it under the 19th, disagreeing with the SAME page's own `{{today}}` filter,
 * which already resolves Eastern. Pinned in groupBy.test.ts (C61), including the January control
 * (EST) so a hardcoded `-04:00` cannot pass by only ever being checked in summer.
 */
import { MONDAY_OFFSET, STAFF_ZONE, addDays, wallClock, type CalendarDay } from './viewQuery';

export type GroupGranularity = 'year' | 'quarter' | 'month' | 'week' | 'day';

export const GROUP_GRANULARITIES: readonly GroupGranularity[] = ['year', 'quarter', 'month', 'week', 'day'];

/** The sentinel group key for a null/unparseable value (M6) — never a real calendar key, so it can
 * never collide with one, and always sorts last via `compareGroupKeys` below. */
export const NO_GROUP_KEY = '\u0000__TL_NO_GROUP__';

export interface GroupBucket {
  /** Stable, lexically-sortable identity — zero-padded so string comparison equals chronological
   * order (e.g. month '2026-03' sorts before '2026-11'). */
  key: string;
  /** Display label, e.g. "March 2026", "Q1 2026", "Week of Mar 2, 2026". */
  label: string;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function isoDay(day: CalendarDay): string {
  return `${day.y}-${pad2(day.m)}-${pad2(day.d)}`;
}

function shortLabel(day: CalendarDay): string {
  return `${MONTH_NAMES[day.m - 1].slice(0, 3)} ${day.d}, ${day.y}`;
}

/** Parse `value` to an epoch-ms instant, or `null` on anything null/empty/unparseable (M6). */
function toInstant(value: string | number | Date | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const ms = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(String(value));
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Buckets `value` into its group for `granularity`, resolved in the staff-interactive (Eastern)
 * zone (M5). A null/empty/unparseable value buckets into the "(blank)" sentinel (M6) — the row is
 * never dropped.
 */
export function bucketFor(
  value: string | number | Date | null | undefined,
  granularity: GroupGranularity
): GroupBucket {
  const instant = toInstant(value);
  if (instant === null) return { key: NO_GROUP_KEY, label: '(blank)' };

  const w = wallClock(STAFF_ZONE, instant);
  const today: CalendarDay = { y: w.y, m: w.m, d: w.d };

  switch (granularity) {
    case 'year':
      return { key: `${w.y}`, label: `${w.y}` };
    case 'quarter': {
      const q = Math.floor((w.m - 1) / 3) + 1;
      return { key: `${w.y}-Q${q}`, label: `Q${q} ${w.y}` };
    }
    case 'month':
      return { key: `${w.y}-${pad2(w.m)}`, label: `${MONTH_NAMES[w.m - 1]} ${w.y}` };
    case 'week': {
      // Monday-based (M4), identical rule to viewQuery.ts's {{weekStart}}.
      const monday = addDays(today, -(MONDAY_OFFSET[w.weekday] ?? 0));
      return { key: isoDay(monday), label: `Week of ${shortLabel(monday)}` };
    }
    case 'day':
      return { key: isoDay(today), label: shortLabel(today) };
  }
}

/** Sort comparator for group bucket KEYS: chronological ascending, "(blank)" always last. */
export function compareGroupKeys(a: string, b: string): number {
  if (a === NO_GROUP_KEY) return b === NO_GROUP_KEY ? 0 : 1;
  if (b === NO_GROUP_KEY) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
}
